import { UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Col, List, notification, Row, Space, Table, Typography, Upload } from 'antd'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'

import {
    SinifCreateInput,
    SinifUpdateInput,
    useSinifTopluEkleMutation,
    useSinifTopluGuncelleMutation,
} from '../../../../../gql/GeneratedGraphqlTypes'

const EXPECTED_COLUMNS = ['id', 'isim', 'kampus_id', 'kampus_adi', 'kategori_id', 'kategori_adi', 'mevcut', 'aktif']

const REQUIRED_FOR_CREATE = ['isim', 'kampus_id', 'kategori_id'] as const
const OPTIONAL_HEADERS = ['id', 'kampus_adi', 'kategori_adi', 'mevcut', 'aktif'] as const
const ALLOWED_HEADERS = [...REQUIRED_FOR_CREATE, ...OPTIONAL_HEADERS] as const

const { Title, Text } = Typography

const ClassImportPage = () => {
    const [file, setFile] = useState<File | null>(null)
    const [tableData, setTableData] = useState<any[]>([])
    const [columns, setColumns] = useState<any[]>([])
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const navigate = useNavigate()
    const [sinifTopluEkle] = useSinifTopluEkleMutation()
    const [sinifTopluGuncelle] = useSinifTopluGuncelleMutation()

    const normalizeHeader = (h: any) =>
        String(h ?? '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_') // "kampus id" -> "kampus_id"
            .replace(/_+/g, '_') // "__" -> "_"

    const handleFileChange = (info: any) => {
        const f = info.fileList.length > 0 ? info.fileList[0].originFileObj : null
        if (f) {
            setFile(f)
            readExcel(f)
        } else {
            setFile(null)
            setTableData([])
            setColumns([])
            setErrorMessages({})
        }
    }

    const handleRemove = () => {
        setFile(null)
        setTableData([])
        setColumns([])
        setErrorMessages({})
    }

    const readExcel = (file: File) => {
        const reader = new FileReader()
        reader.readAsBinaryString(file)

        reader.onload = (event) => {
            try {
                const data = event.target?.result
                const workbook = XLSX.read(data, { type: 'binary' })
                const sheetName = workbook.SheetNames[0]
                const sheet = workbook.Sheets[sheetName]

                const jsonData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 })

                if (!jsonData.length) {
                    notification.error({ message: 'Dosya boş veya okunamıyor.' })
                    return
                }

                const [headersRaw, ...rows] = jsonData
                const headers = (headersRaw ?? []).map(normalizeHeader).filter(Boolean)

                if (!headers.length || rows.length === 0) {
                    notification.error({ message: 'Geçersiz dosya formatı' })
                    return
                }

                // Fazla/geçersiz başlık kontrolü
                const extra = headers.filter((h) => !(ALLOWED_HEADERS as readonly string[]).includes(h))
                if (extra.length) {
                    const msg = `Fazla/Geçersiz başlıklar: ${extra.join(', ')} | İzin verilen başlıklar: ${(
                        ALLOWED_HEADERS as readonly string[]
                    ).join(', ')}`
                    setErrorMessages({ 0: msg })
                    setTableData([])
                    setColumns([])
                    notification.error({ message: 'Başlıklar hatalı', description: msg, duration: 10 })
                    return
                }

                // Dosyada gerçekten bulunan başlıklar (preview/mapping)
                const presentHeaders = (ALLOWED_HEADERS as readonly string[]).filter((h) => headers.includes(h))

                // Boş satırları ayıkla
                const nonEmptyRows = rows.filter((row) =>
                    row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')
                )

                const newErrorMessages: Record<number, string> = {}
                const validRows: any[] = []

                nonEmptyRows.forEach((row, rowIndex) => {
                    const formattedRow: Record<string, any> = {}

                    presentHeaders.forEach((header) => {
                        const originalIndex = headers.indexOf(header)
                        formattedRow[header] = row[originalIndex]
                    })

                    const errors: string[] = []

                    const idVal = formattedRow['id']
                    const hasId = idVal !== undefined && idVal !== null && String(idVal).trim() !== ''

                    /**
                     * UPDATE: id varsa hiçbir alan zorunlu değil.
                     * CREATE: id yoksa create için zorunlu alanlar kontrol edilir.
                     */
                    if (!hasId) {
                        // Create için: isim + kampus_id + kategori_id zorunlu
                        const isimVal = formattedRow['isim']
                        const kampusId = formattedRow['kampus_id']
                        const kategoriId = formattedRow['kategori_id']

                        if (!isimVal || !String(isimVal).trim()) errors.push('isim zorunlu')
                        if (!kampusId || isNaN(Number(kampusId))) errors.push('kampus_id eksik veya geçersiz')
                        if (!kategoriId || isNaN(Number(kategoriId))) errors.push('kategori_id eksik veya geçersiz')
                    }

                    if (errors.length) {
                        newErrorMessages[rowIndex] = `Satır ${rowIndex + 2}: ${errors.join(', ')}`
                    } else {
                        validRows.push(formattedRow)
                    }
                })

                if (Object.keys(newErrorMessages).length) {
                    setErrorMessages(newErrorMessages)
                    setTableData([])
                    setColumns([])
                    notification.error({ message: 'Hatalı satırlar bulundu. Lütfen düzeltin.' })
                    return
                }

                setErrorMessages({})

                // Preview kolonları: sadece dosyada var olan başlıklar (presentHeaders)
                setColumns(
                    presentHeaders.map((header, index) => ({
                        title: header,
                        dataIndex: `col${index}`,
                        key: `col${index}`,
                    }))
                )

                setTableData(
                    validRows.map((r, rowIndex) => {
                        const rowData: Record<string, any> = { key: rowIndex }
                        presentHeaders.forEach((h, colIndex) => {
                            rowData[`col${colIndex}`] = r[h]
                        })
                        return rowData
                    })
                )
            } catch (e) {
                notification.error({ message: 'Dosya okunurken hata oluştu.' })
            }
        }
    }

    // ---- SUBMIT (aynı kalsın) ----
    const handleSubmit = async () => {
        if (isSubmitting) return
        setIsSubmitting(true)
        if (!file) {
            notification.error({ message: 'Lütfen bir dosya seçin!' })
            return
        }
        if (tableData.length === 0) {
            notification.error({ message: 'Tabloda veri bulunmuyor!' })
            return
        }

        const getCell = (row: any, title: string) => {
            const colIndex = columns.findIndex((c) => c.title === title)
            if (colIndex === -1) return undefined
            return row[`col${colIndex}`]
        }

        const hasText = (v: any) => v !== null && v !== undefined && String(v).trim() !== ''

        const toStrOpt = (v: any) => (hasText(v) ? String(v).trim() : undefined)

        const toIntOpt = (v: any) => {
            if (!hasText(v)) return undefined
            const n = Number(String(v).trim())
            return Number.isFinite(n) ? Math.trunc(n) : undefined
        }

        const toBoolOpt = (v: any) => {
            if (!hasText(v)) return undefined
            if (typeof v === 'boolean') return v

            const s = String(v).trim().toLowerCase()
            if (['1', 'true', 'evet', 'yes', 'y', 'aktif'].includes(s)) return true
            if (['0', 'false', 'hayir', 'hayır', 'no', 'n', 'pasif'].includes(s)) return false
            return undefined
        }

        const submitErrors: string[] = []
        const createList: SinifCreateInput[] = []
        const updateList: SinifUpdateInput[] = []

        tableData.forEach((row, i) => {
            const rowNo = i + 2

            const id = toIntOpt(getCell(row, 'id'))
            const isimRaw = getCell(row, 'isim')
            const kampusIdRaw = getCell(row, 'kampus_id')
            const kategoriIdRaw = getCell(row, 'kategori_id')
            const mevcutRaw = getCell(row, 'mevcut')
            const aktifRaw = getCell(row, 'aktif')

            const mevcut = toIntOpt(mevcutRaw)
            const aktif = toBoolOpt(aktifRaw)

            // UPDATE: id varsa partial gönder
            if (id !== undefined && id > 0) {
                const isim = toStrOpt(isimRaw)
                const sube_id = toIntOpt(kampusIdRaw)
                const sinif_kategorisi_id = toIntOpt(kategoriIdRaw)

                const payload: SinifUpdateInput = {
                    id,
                    ...(isim !== undefined ? { isim } : {}),
                    ...(sube_id !== undefined ? { sube_id } : {}),
                    ...(sinif_kategorisi_id !== undefined ? { sinif_kategorisi_id } : {}),
                    ...(mevcut !== undefined ? { mevcut } : {}),
                    ...(aktif !== undefined ? { aktif } : {}),
                }

                // İstersen burada da "id dışında alan yok" kontrolünü KALDIRDIK.
                updateList.push(payload)
                return
            }

            // CREATE: zorunlular
            const isimReq = toStrOpt(isimRaw)
            const subeReq = toIntOpt(kampusIdRaw)
            const katReq = toIntOpt(kategoriIdRaw)

            if (!isimReq) submitErrors.push(`Satır ${rowNo}: isim zorunlu`)
            if (!subeReq) submitErrors.push(`Satır ${rowNo}: kampus_id zorunlu ve sayı olmalı`)
            if (!katReq) submitErrors.push(`Satır ${rowNo}: kategori_id zorunlu ve sayı olmalı`)

            if (!isimReq || !subeReq || !katReq) return

            const payload: SinifCreateInput = {
                isim: isimReq,
                sube_id: subeReq,
                sinif_kategorisi_id: katReq,
                ...(mevcut !== undefined ? { mevcut } : {}),
                ...(aktif !== undefined ? { aktif } : {}),
            }

            createList.push(payload)
        })

        if (submitErrors.length) {
            notification.error({
                message: 'Hatalı satırlar var',
                description:
                    submitErrors.slice(0, 8).join(' | ') + (submitErrors.length > 8 ? ` (+${submitErrors.length - 8})` : ''),
                duration: 10,
            })
            return
        }

        try {
            if (createList.length) {
                const res = await sinifTopluEkle({ variables: { input: createList } })
                if (res.errors?.length || !res.data?.sinifTopluEkle?.success) {
                    notification.error({
                        message: 'Toplu Ekle başarısız',
                        description: res.data?.sinifTopluEkle?.message || res.errors?.[0]?.message || 'Bilinmeyen hata',
                        duration: 10,
                    })
                    return
                }
            }

            if (updateList.length) {
                const res = await sinifTopluGuncelle({ variables: { input: updateList } })
                if (res.errors?.length || !res.data?.sinifTopluGuncelle?.success) {
                    notification.error({
                        message: 'Toplu Güncelle başarısız',
                        description: res.data?.sinifTopluGuncelle?.message || res.errors?.[0]?.message || 'Bilinmeyen hata',
                        duration: 10,
                    })
                    return
                }
            }

            notification.success({
                message: 'Başarılı',
                description: `Ekleme: ${createList.length} satır, Güncelleme: ${updateList.length} satır`,
                duration: 6,
            })
            navigate(-1)
        } catch (e: any) {
            notification.error({
                message: 'Bağlantı Hatası',
                description: e?.message || 'Sunucuya bağlanırken hata oluştu.',
                duration: 10,
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="okul-siparis-import-page">
            <Card title="Sınıflar İçe Aktar" bordered={false}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Text>
                        Bu içe aktarıcı ilgili alanları içe aktaracaktır: <Text code>{EXPECTED_COLUMNS.join(', ')}</Text>
                    </Text>

                    <Row>
                        <Col span={4}>
                            <Upload
                                beforeUpload={() => false}
                                onChange={handleFileChange}
                                fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
                                onRemove={handleRemove}
                                showUploadList={{ showRemoveIcon: true }}
                            >
                                <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                            </Upload>
                        </Col>
                    </Row>

                    {tableData.length > 0 && (
                        <>
                            <Title level={5}>Önizleme:</Title>
                            <Table columns={columns} dataSource={tableData} pagination={{ pageSize: 5 }} />
                        </>
                    )}

                    {Object.keys(errorMessages).length > 0 && (
                        <List
                            header={<Title level={5}>Hatalar</Title>}
                            bordered
                            dataSource={Object.values(errorMessages)}
                            renderItem={(item) => (
                                <List.Item>
                                    <Alert message={item} type="error" showIcon />
                                </List.Item>
                            )}
                        />
                    )}

                    <div className="okul-yonetim-footer-buttons">
                        <Button className="back-btn" onClick={() => navigate(-1)}>
                            Geri
                        </Button>
                        <Button className="update-btn" type="primary" onClick={handleSubmit} loading={isSubmitting} disabled={!file || Object.keys(errorMessages).length > 0}>
                            Kaydet
                        </Button>
                    </div>
                </Space>
            </Card>
        </div>
    )
}

export default ClassImportPage
